import { ChevronRightIcon } from '@heroicons/react/20/solid'
import {
  ChartBarSquareIcon,
  ChevronLeftIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Divider, Text } from '../../../common/designs'
import { useDashboard } from '../../../common/hooks'
import { AdminMainPathEnum, MainPathEnum } from '../../../common/types/routing'
import CompanySelect from '../MainNavigation/CompanySelect'

interface NavBranch {
  name: string
  href?: string
  icon: React.ElementType
  active: boolean
  isHidden?: boolean
  children?: NavTwig[]
}

interface NavTwig {
  name: string
  href: string
  active: boolean
  isHidden?: boolean
}

const Sidebar = () => {
  const location = useLocation()
  const { user } = useDashboard()
  const isNotSuperuser = !user.isSuperuser
  const [isOpen, setIsOpen] = useState<Record<number, boolean>>({})

  const isPathActive = (href?: string): boolean => {
    if (!href) return false
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  useEffect(() => {
    const newIsOpen: Record<number, boolean> = {}
    updatedNav.forEach((item, i) => {
      if (item.children) newIsOpen[i] = item.children.some((child) => child.active)
    })
    setIsOpen(newIsOpen)
  }, [location]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateActiveNav = (navItems: NavBranch[]): NavBranch[] => {
    return navItems.map((item) => {
      const updatedItem: NavBranch = {
        ...item,
        active: isPathActive(item.href),
      }

      if (item.children) {
        updatedItem.children = item.children.map((child) => ({
          ...child,
          active: isPathActive(child.href),
        }))
      }

      return updatedItem
    })
  }

  const onBranchClick = (branchIndex: number) => {
    setIsOpen((prev) => ({
      ...prev,
      [branchIndex]: !prev[branchIndex],
    }))
  }

  const onNavigationItemClick = (branchIndex: number) => {
    setIsOpen({ [branchIndex]: true })
  }

  const navigation: NavBranch[] = [
    {
      name: 'Service Setup',
      icon: WrenchIcon,
      active: true,
      children: [
        { name: 'Get Started', href: AdminMainPathEnum.GET_STARTED, active: true },
        { name: 'Agent Deployment', href: AdminMainPathEnum.INSTALLATION_CENTER, active: false },
        {
          name: 'Configure Data Collection',
          href: AdminMainPathEnum.DATA_COLLECTION,
          active: false,
        },
        {
          name: 'Structured Processes',
          href: AdminMainPathEnum.STRUCTURED_PROCESSES,
          active: false,
        },
        { name: 'ARIS Integration', href: AdminMainPathEnum.ARIS_INTEGRATION, active: false },
      ],
    },
    {
      name: 'Dashboard Settings',
      icon: ComputerDesktopIcon,
      active: false,
      children: [
        { name: 'Company Information', href: AdminMainPathEnum.COMPANY, active: false },
        { name: 'User Management', href: AdminMainPathEnum.USERS, active: false },
        { name: 'Development Opportunities', href: AdminMainPathEnum.OPPORTUNITIES, active: false },
        { name: 'Team Grouping', href: AdminMainPathEnum.TEAM_GROUPS, active: false },
        { name: 'Data Deletion', href: AdminMainPathEnum.DATA_DELETION, active: false },
      ],
    },
    {
      name: 'Monitoring',
      icon: ChartBarSquareIcon,
      active: false,
      children: [
        { name: 'Overview', href: AdminMainPathEnum.MONITORING_OVERVIEW, active: false },
        { name: 'Computer Connectivity', href: AdminMainPathEnum.AGENTS, active: false },
      ],
    },
    {
      name: 'Superuser Tools',
      icon: ShieldCheckIcon,
      active: false,
      children: [
        { name: 'Superuser Guide', href: AdminMainPathEnum.SUPERUSER_GUIDE, active: false },
        {
          name: 'Add Company',
          href: AdminMainPathEnum.COMPANY_CREATION,
          active: false,
          isHidden: isNotSuperuser,
        },
        {
          name: 'Performance Readings',
          href: AdminMainPathEnum.PERFORMANCE,
          active: false,
          isHidden: isNotSuperuser,
        },
      ],
    },
  ]

  const updatedNav = useMemo(() => updateActiveNav(navigation), [location]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside className='flex h-full min-h-0 w-72 shrink-0 flex-col border-r border-slate-200/80 bg-slate-50/70'>
      <div className='shrink-0 space-y-3 px-4 pb-3 pt-4'>
        <div className='flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm'>
          <Link
            to={MainPathEnum.DEFAULT}
            className='flex size-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 outline-none ring-offset-2 transition hover:border-primary-200 hover:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500'
            aria-label='Go to dashboard'
          >
            <img
              className='size-5'
              alt=''
              src='/images/pi-logo-color.png'
              aria-hidden={true}
            />
          </Link>

          <div className='min-w-0'>
            <Text variant='span' size='m' weight={700} className='block text-slate-900'>
              Admin Panel
            </Text>
            <Text variant='span' size='xs' color='lightGray' className='block'>
              Configuration workspace
            </Text>
          </div>
        </div>

        <div className='rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm'>
          <Text
            variant='span'
            size='xs'
            weight={600}
            color='lightGray'
            className='mb-1.5 block px-1'
          >
            Active Company
          </Text>
          <div>
            <CompanySelect />
          </div>
        </div>

        <Divider className='my-0.5' color='lightGray' />
      </div>

      <nav className='min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2'>
        <ul className='space-y-3'>
          {updatedNav.map((item, i: number) => {
            if (item.isHidden) return null

            return (
              <li
                key={item.name}
                className='rounded-xl border border-slate-200/80 bg-white p-1.5 shadow-sm'
              >
                {!item.children ? (
                  <Link
                    to={item.href as string}
                    className={clsx(
                      item.active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-primary-700',
                      'group flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-semibold leading-6 transition',
                    )}
                  >
                    <item.icon
                      className={clsx(
                        'size-5 shrink-0 text-slate-400 group-hover:text-primary-600',
                        item.active && 'text-primary-600',
                      )}
                      aria-hidden='true'
                    />
                    {item.name}
                  </Link>
                ) : (
                  <>
                    <button
                      type='button'
                      onClick={() => onBranchClick(i)}
                      className={clsx(
                        item.active || isOpen[i]
                          ? 'bg-slate-100 text-slate-900 hover:text-primary-700'
                          : 'text-slate-700',
                        'group flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-left text-sm font-semibold leading-6 transition hover:bg-slate-50 hover:text-primary-700',
                      )}
                    >
                      <item.icon
                        className={clsx(
                          'size-5 shrink-0 text-slate-400 group-hover:text-primary-600',
                          (item.active || isOpen[i]) && 'text-primary-600',
                        )}
                        aria-hidden='true'
                      />
                      {item.name}
                      <ChevronRightIcon
                        className={clsx(
                          isOpen[i] ? 'rotate-90 text-slate-500' : 'text-slate-400',
                          'ml-auto size-4 shrink-0 transition-transform duration-200 group-hover:text-primary-600',
                        )}
                        aria-hidden='true'
                      />
                    </button>

                    {isOpen[i] ? (
                      <ul className='ml-2 mt-1.5 space-y-1 border-l border-slate-200 pl-2'>
                        {item.children?.map((subItem) => {
                          if (subItem?.isHidden) return null

                          return (
                            <li key={subItem.name}>
                              <Link
                                to={subItem.href}
                                onClick={() => onNavigationItemClick(i)}
                                className={clsx(
                                  subItem.active
                                    ? 'bg-primary-50 text-primary-700'
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-primary-700',
                                  'block rounded-md px-2.5 py-1.5 text-sm leading-6 transition',
                                )}
                              >
                                {subItem.name}
                              </Link>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </nav>

      <footer className='shrink-0 border-t border-slate-200/80 bg-white/80 px-4 py-3.5 backdrop-blur'>
        <Link
          to={MainPathEnum.DEFAULT}
          className='flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-800 hover:shadow'
        >
          <ChevronLeftIcon className='size-4 shrink-0 text-primary-600' aria-hidden />
          Back to Dashboard
        </Link>
      </footer>
    </aside>
  )
}

export default Sidebar

